import { useQueryParams } from '@calendly/hooks';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { FixedContainer } from '@calendly/ui/components/fixed-container';
import { AddMiniIcon, ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon, ArrowRightIcon } from '@calendly/ui/components/icons';
import { Group, GroupTitle, PopupDropdownMenu } from '@calendly/ui/components/menu';
import { TitleText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { colorTextStandard } from '@calendly/ui/theme';
import { mergeRefs } from '@calendly/ui/utils/merge-refs';
import classNames from 'classnames';
import { forwardRef, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { moveToChangeSubscription } from 'actions/billing/subscription_modal';
import { isAvailableFeatureSelector } from 'features/billing';
import { Option } from 'features/event_type_creation_dropdown';
import { trackEditorOpen } from 'features/event_type_editor';
import { useTrack } from 'hooks/analytics_events/useTrack';
import { usePortaledMenuPosition } from 'hooks/use_portaled_menu_position';
import { etEditorOverlayZIndex } from 'shared/modal_stacking';
import { useCreateBaseEventType } from './hooks';

// Phone PopupDropdownMenu re-portals via FixedContainer (default z-index 15), under the
// Settings mobile shell (zIndexSticky). Elevate with the shared overlay layer.
const createMenuLayerStyle = {
  zIndex: etEditorOverlayZIndex
};
const containerCss = "cbpjjo4";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const buttonContainerCss = "b6hiz4";
const ButtonContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(buttonContainerCss, props.className)} />;
});
const styledButtonCss = "s1u3f6o6";
const StyledButton = forwardRef((props, ref) => <Button ref={ref} {...props} className={classNames(styledButtonCss, props.className)} />);
const noWrapCss = "n1jsz4y8";
const NoWrap = forwardRef((props, ref) => <span ref={ref} {...props} className={classNames(noWrapCss, props.className)} />);
const portaledPopupMenuCss = "p2v8tds";
const StyledPopupDropdownMenu = forwardRef((props, ref) => {
  const isMobile = useDeviceType('isPhone');
  return <PopupDropdownMenu ref={ref} {...props} {...{
    style: createMenuLayerStyle,
    mobileMenuStyle: createMenuLayerStyle
  }} className={classNames(isMobile ? '' : portaledPopupMenuCss, props.className)} />;
});
const portalDropdownContainerCss = "p19tyzo8";
const PortalDropdownContainer = ({
  anchorRef,
  dropdownRef,
  isOpen,
  children
}) => {
  const menuRef = useRef(null);
  usePortaledMenuPosition(isOpen, anchorRef, menuRef, {
    placement: 'bottom-end',
    gap: 0,
    useViewportBounds: true
  });
  return <FixedContainer fixedWhen ref={mergeRefs(dropdownRef, menuRef)} className={portalDropdownContainerCss}>
      {children}
    </FixedContainer>;
};
const eventTypesContainerCss = "e1vov0q0";
const EventTypesContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(eventTypesContainerCss, props.className)} />;
});
const DropdownContent = ({
  handleOptionClick,
  dropdownId,
  createButtonId
}) => <StyledPopupDropdownMenu role="menu" id={dropdownId} aria-labelledby={createButtonId}>
    <Group>
      <GroupTitle>
        <TitleText variant="eyebrow" color={colorTextStandard}>
          {I18n.t('publisher.managed_event_types.title')}
        </TitleText>
      </GroupTitle>
      <EventTypesContainer role="menu">
        <Option kind="solo" type="ManagedEventType" name={I18n.t('publisher.event_types.create.one_on_one.name')} description={HtmlParser(I18n.t('publisher.event_types.create.one_on_one.description'), {
        ArrowRightIcon
      })} example={I18n.t('publisher.event_types.create.one_on_one.example')} onClick={() => handleOptionClick('solo')} />
        <Option kind="group" type="ManagedEventType" name={I18n.t('publisher.event_types.create.group.name')} description={HtmlParser(I18n.t('publisher.event_types.create.group.description'), {
        ArrowRightIcon
      })} example={I18n.t('publisher.event_types.create.group.example')} onClick={() => handleOptionClick('group')} />
      </EventTypesContainer>
    </Group>
  </StyledPopupDropdownMenu>;
export const CreateDropdownButton = ({
  disabled,
  skipMetPageNavigation = false
}) => {
  const dispatch = useDispatch();
  const track = useTrack();
  const {
    new: isOpenByDefault
  } = useQueryParams();
  const createButtonId = 'managed-events-create-button';
  const [toggle, Dropdown, isOpen, {
    dropdownId,
    dropdownRef
  }] = useDropdown(Boolean(isOpenByDefault), true);
  const {
    UserGroupPopup,
    handleDifferentUserTypeFlows
  } = useCreateBaseEventType({
    skipMetPageNavigation
  });
  const areManagedEventsAvailable = useSelector(state => isAvailableFeatureSelector(state, {
    featureKey: 'managed_events'
  }));
  const anchorRef = useRef(null);
  const trackCreateMETV3 = ({
    kind
  }) => {
    const etKind = kind || 'solo';
    trackEditorOpen({
      track,
      destination: 'create',
      source: 'admin center managed et create',
      kind: etKind,
      type: 'ManagedEventType'
    });
  };
  const handleClick = kind => {
    const createNewMET = () => {
      handleDifferentUserTypeFlows(kind);
      trackCreateMETV3({
        kind
      });
    };
    if (areManagedEventsAvailable) {
      createNewMET();
    } else {
      dispatch(moveToChangeSubscription({
        featureKey: 'managed_events',
        preface: true,
        onSuccess: async () => {
          createNewMET();
        }
      }));
    }
  };
  return <>
      <Container ref={anchorRef}>
        <ButtonContainer>
          <StyledButton id={createButtonId} size="small" onClick={toggle} aria-haspopup="menu" aria-expanded={isOpen} aria-controls={dropdownId} data-id="managed-events-create-button" disabled={disabled}>
            <AddMiniIcon size="large" />
            <NoWrap>{I18n.t('publisher.action.create')}</NoWrap>
            {isOpen ? <ArrowChevronUpMiniIcon /> : <ArrowChevronDownMiniIcon />}
          </StyledButton>
        </ButtonContainer>
        <Dropdown>
          <PortalDropdownContainer anchorRef={anchorRef} dropdownRef={dropdownRef} isOpen={isOpen}>
            <DropdownContent handleOptionClick={handleClick} dropdownId={dropdownId} createButtonId={createButtonId} />
          </PortalDropdownContainer>
        </Dropdown>
      </Container>
      {UserGroupPopup}
    </>;
};

require("./create_dropdown_button.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./create_dropdown_button.js");